import React, { useRef, useEffect } from 'react';
import './BrowserView.css';
import { isWeb } from '../utils/platform';

function BrowserView({ url, setUrl }) {
  const urlInput = useRef();
  const containerRef = useRef();
  const urlBarRef = useRef();

  // Send the default URL and bounds on mount
  useEffect(() => {
    if (window.electronAPI && window.electronAPI.loadExternalURL) {
      sendUrlAndBounds(url);
    }
    // eslint-disable-next-line
  }, []);

  // Helper to send URL and bounds
  const sendUrlAndBounds = (targetUrl) => {
    console.log('sendUrlAndBounds called with:', targetUrl);
    console.log('containerRef.current:', containerRef.current);
    console.log('window.electronAPI:', window.electronAPI);
    
    // Use the parent .browser-only-content container for bounds
    let boundsRef = containerRef.current;
    let parent = boundsRef;
    while (parent && !parent.classList.contains('browser-only-content')) {
      parent = parent.parentElement;
    }
    
    // Fallback: if we can't find the parent, use the current container
    if (!parent) {
      parent = boundsRef;
      console.log('Using fallback container:', parent);
    }
    
    console.log('Found parent container:', parent);
    
    if (window.electronAPI && window.electronAPI.loadExternalURL && parent) {
      const rect = parent.getBoundingClientRect();
      let urlBarHeight = 0;
      if (urlBarRef.current) {
        urlBarHeight = urlBarRef.current.getBoundingClientRect().height;
      }
      
      console.log('Bounds calculated:', {
        x: Math.round(rect.left),
        y: Math.round(rect.top + urlBarHeight),
        width: Math.round(rect.width),
        height: Math.round(rect.height - urlBarHeight)
      });
      
      try {
        window.electronAPI.loadExternalURL(targetUrl, {
          x: Math.round(rect.left),
          y: Math.round(rect.top + urlBarHeight),
          width: Math.round(rect.width),
          height: Math.round(rect.height - urlBarHeight)
        });
        console.log('loadExternalURL called successfully');
      } catch (error) {
        console.error('Error calling loadExternalURL:', error);
      }
    } else {
      console.log('Missing requirements:', {
        hasElectronAPI: !!window.electronAPI,
        hasLoadExternalURL: !!(window.electronAPI && window.electronAPI.loadExternalURL),
        hasParent: !!parent
      });
    }
  };

  const handleLoad = (e) => {
    e.preventDefault();
    const newUrl = urlInput.current.value;
    setUrl(newUrl);
    
    if (window.electronAPI && window.electronAPI.loadExternalURL) {
      // Load URL in-app (not in modal)
      sendUrlAndBounds(newUrl);
    } else {
      // Fallback for browser mode
      console.log('Running in browser mode - opening URL in new tab');
      window.open(newUrl, '_blank');
    }
  };

  const handleBookmarkClick = (bookmarkUrl) => {
    setUrl(bookmarkUrl);
    if (urlInput.current) {
      urlInput.current.value = bookmarkUrl;
    }
    
    if (window.electronAPI && window.electronAPI.loadExternalURL) {
      // Load bookmark URL in-app (not in modal)
      sendUrlAndBounds(bookmarkUrl);
    } else {
      // Fallback for browser mode
      console.log('Running in browser mode - opening bookmark in new tab');
      window.open(bookmarkUrl, '_blank');
    }
  };

  if (isWeb) {
    return (
      <div className="browser-view-card" ref={containerRef}>
        <div className="webview-container" style={{ padding: 24, textAlign: 'center' }}>
          <p style={{ marginBottom: 16, fontSize: 16 }}>
            In-browser viewing is available in the Fiddyscript desktop app.
          </p>
          <p style={{ marginBottom: 16, color: '#666' }}>
            You can open links in a new tab using the URL bar below.
          </p>
          <form className="browser-url-bar-top" onSubmit={handleLoad} autoComplete="off" style={{ marginTop: 16, justifyContent: 'center' }}>
            <input
              ref={urlInput}
              type="text"
              defaultValue={url}
              placeholder="Enter URL (e.g. https://example.com)"
              className="browser-url-input"
              autoFocus={false}
              style={{ maxWidth: 400 }}
            />
            <button type="submit" className="browser-url-load-btn">Open in new tab</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="browser-view-card" ref={containerRef}>
      {/* Always render the browser URL bar at the top */}
      <div className="browser-url-bar-top-wrapper" ref={urlBarRef}>
        {/* Bookmark links */}
        <div className="browser-bookmarks">
          <button 
            type="button" 
            className="browser-bookmark-btn" 
            onClick={() => handleBookmarkClick('https://fideantech.com')}
            title="Fideantech"
          >
            Fideantech
          </button>
          <button 
            type="button" 
            className="browser-bookmark-btn" 
            onClick={() => handleBookmarkClick('https://www.linkedin.com/')}
            title="LinkedIn"
          >
            LinkedIn
          </button>
          <button 
            type="button" 
            className="browser-bookmark-btn" 
            onClick={() => handleBookmarkClick('https://www.facebook.com/')}
            title="Facebook"
          >
            Facebook
          </button>
          <button 
            type="button" 
            className="browser-bookmark-btn" 
            onClick={() => handleBookmarkClick('https://business.facebook.com/')}
            title="Business Suite"
          >
            Business Suite
          </button>
          <button 
            type="button" 
            className="browser-bookmark-btn" 
            onClick={() => handleBookmarkClick('https://canva.com')}
            title="Canva"
          >
            Canva
          </button>
          <button 
            type="button" 
            className="browser-bookmark-btn" 
            onClick={() => handleBookmarkClick('https://www.instagram.com/')}
            title="Instagram"
          >
            Instagram
          </button>
          <button 
            type="button" 
            className="browser-bookmark-btn" 
            onClick={() => handleBookmarkClick('https://web.whatsapp.com/')}
            title="WhatsApp Web"
          >
            WhatsApp Web
          </button>
          <button 
            type="button" 
            className="browser-bookmark-btn" 
            onClick={() => handleBookmarkClick('https://www.tiktok.com/')}
            title="TikTok"
          >
            TikTok
          </button>
          <button 
            type="button" 
            className="browser-bookmark-btn" 
            onClick={() => handleBookmarkClick('https://x.com/')}
            title="X"
          >
            X
          </button>
          <button 
            type="button" 
            className="browser-bookmark-btn" 
            onClick={() => handleBookmarkClick('https://www.youtube.com/')}
            title="YouTube"
          >
            YouTube
          </button>
        </div>
        <form className="browser-url-bar-top" onSubmit={handleLoad} autoComplete="off">
          <input
            ref={urlInput}
            type="text"
            defaultValue={url}
            placeholder="Enter URL (e.g. https://fideantech.com)"
            className="browser-url-input"
            autoFocus={false}
          />
          <button
            type="button"
            className="browser-url-load-btn"
            onClick={() => {
              const currentUrl = urlInput.current.value;
              if (window.electronAPI && window.electronAPI.openInWebview2) {
                // Launch WebView2 in modal
                window.electronAPI.openInWebview2(currentUrl);
              } else {
                // Fallback for browser mode
                console.log('Running in browser mode - opening Secure Browser in new tab');
                window.open(currentUrl, '_blank');
              }
            }}
            title="Open in Secure Browser (Edge WebView2)"
          >
            Secure Browser
          </button>
        </form>
        <div className="browser-session-controls">
          <button 
            type="button" 
            className="browser-session-btn" 
            onClick={() => {
              if (window.electronAPI && window.electronAPI.clearAllSessions) {
                if (confirm('This will log you out from all websites. Continue?')) {
                  window.electronAPI.clearAllSessions();
                }
              }
            }}
            title="Logout from all sites"
          >
            Clear Sessions
          </button>
        </div>
      </div>
      <div className="webview-container">
        {/* External site will be loaded in Electron's BrowserView, not here */}
      </div>
    </div>
  );
}

export default BrowserView; 